import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, KeyRound, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setErr("Please enter your email address."); return; }
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      // The endpoint always 200s — only network / 4xx-on-format errors land here.
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F6F8FE] via-white to-[#F6F8FE] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-7">
        <button onClick={() => navigate("/login")} className="text-xs text-brand-inkMuted hover:text-brand-purple flex items-center gap-1 mb-5" data-testid="fp-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </button>
        {sent ? (
          <div className="text-center py-6" data-testid="fp-sent">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-3"><CheckCircle2 className="w-7 h-7 text-[#2E7D32]" /></div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Check your inbox</h1>
            <p className="text-sm text-brand-inkMuted mt-2 leading-relaxed">If <strong className="text-foreground">{email}</strong> is registered, we just sent a password reset link. The link expires in 30 minutes. Don't forget to check your spam folder.</p>
            <div className="mt-6"><Link to="/login"><Button className="rounded-2xl bg-brand-purple text-white w-full">Return to login</Button></Link></div>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl bg-brand-purpleLight/60 flex items-center justify-center mb-3"><KeyRound className="w-6 h-6 text-brand-purple" /></div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Forgot your password?</h1>
            <p className="text-sm text-brand-inkMuted mt-1.5">Enter your email and we'll send a reset link.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="font-heading">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-2xl h-11 mt-1.5" autoFocus data-testid="fp-email" />
              </div>
              {err && <p className="text-sm text-[#C62828]" data-testid="fp-err">{err}</p>}
              <Button type="submit" disabled={busy} className="w-full rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11" data-testid="fp-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : "Send reset link"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
